"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  HelpCircle,
  Sparkles,
  Crown,
  Mic,
  Stethoscope,
  Trophy,
  Target,
  Building2,
  Heart,
  Lock,
  ShieldCheck,
  Globe2,
  BookOpen,
  ArrowRight,
  GraduationCap,
  Briefcase,
  GitBranch,
  PenLine,
  MessageSquare,
  Brain,
  Activity,
  Compass,
  Layers,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const FAQ_SUBPAGES = [
  {
    title: "About Dr. Jyoti Kush",
    description: "Biographical questions, credentials, career trajectory, and the physician-to-COO journey.",
    href: "/faq/about-dr-jyoti-kush/",
    icon: Crown,
    accent: "red",
    questions: 50,
  },
  {
    title: "Services & Engagements",
    description: "Advisory, board service, operational consulting, mentorship, and corporate workshops.",
    href: "/faq/services/",
    icon: Briefcase,
    accent: "purple",
    questions: 31,
  },
  {
    title: "Speaking & Keynotes",
    description: "Signature topics, booking process, formats, and past engagements on global stages.",
    href: "/faq/speaking/",
    icon: Mic,
    accent: "cyan",
    questions: 25,
  },
  {
    title: "Leadership & Governance",
    description: "Clinical leadership methodology, women in leadership, and operational philosophy.",
    href: "/faq/leadership/",
    icon: Trophy,
    accent: "emerald",
    questions: 34,
  },
  {
    title: "Operational Excellence",
    description: "The engineered system that produces verified outcomes — definition, methodology, and measurement.",
    href: "/faq/operational-excellence/",
    icon: Target,
    accent: "amber-gold",
    questions: 27,
  },
  {
    title: "Platforms & Technology",
    description: "The nine proprietary platforms, four-tier architecture, and integration across sovereign operations.",
    href: "/faq/platforms/",
    icon: Layers,
    accent: "denim",
    questions: 30,
  },
  {
    title: "The 70% Project",
    description: "The flagship initiative, operating evidence, and the talent strategy behind majority-female teams.",
    href: "/faq/70-percent-project/",
    icon: Heart,
    accent: "lime",
    questions: 32,
  },
  {
    title: "Women's Empowerment",
    description: "The 70% Operating Model, building diverse teams, and women in C-suite leadership.",
    href: "/faq/womens-empowerment/",
    icon: Sparkles,
    accent: "coral",
    questions: 28,
  },
];

const HEADLINE_QUESTIONS = [
  { q: "Who is Dr. Jyoti Kush?", a: "The Chief Operating Officer of CryptoMize and MaxiMize Infinium — a physician-turned-executive holding an MBBS with specialization in Gynaecology from Delhi, commanding the integrated platform ecosystem and cross-continental teams across eighteen countries.", accent: "red" },
  { q: "What is her success rate?", a: "Fifteen verified campaign victories out of eighteen engagements — an eighty-three point three percent success rate. Over three hundred elite clients served. Zero security incidents across fifteen-plus years.", accent: "purple" },
  { q: "What services does she provide?", a: "Executive advisory, board service, operational consulting, executive mentorship, corporate workshops, keynote speaking, and private engagements — governed by a rigorous five-step methodology.", accent: "cyan" },
  { q: "What is The 70% Project?", a: "The flagship initiative documenting the operating case for building teams of over seventy percent women — not a diversity initiative, a talent strategy validated by a decade of sovereign-scale outcomes.", accent: "emerald" },
  { q: "What is the Penta P's Framework?", a: "The closed-loop strategic sovereignty system of five pillars — Perception, Privacy, Politics, Policing, and Policy — that structures the integrated platform ecosystem and the five-step engagement methodology.", accent: "amber-gold" },
  { q: "How many proprietary platforms does the organization operate?", a: "Nine purpose-built platforms covering intelligence gathering, perception amplification, content distribution, electoral ground truth, governance coordination, crisis response, and sovereign security — all integrated through a central command interface.", accent: "denim" },
  { q: "What recognition has she received?", a: "Presence at the Nobel Peace Prize and the World Economic Forum — stages reserved for those whose work intersects with the most consequential decisions on the planet.", accent: "lime" },
  { q: "How confidential are the engagements?", a: "National security level confidentiality from first contact. Every engagement governed by binding non-disclosure agreements. Operations are compartmentalized — every team sees only what it needs to see, and unified command sees everything.", accent: "coral" },
];

const CATEGORIES = [
  {
    icon: Stethoscope,
    title: "Clinical Method",
    body: "Questions on how medical training — diagnose before treating, triage under pressure, own the outcome — translates into sovereign-scale organizational leadership.",
    accent: "red",
  },
  {
    icon: GitBranch,
    title: "Journey & Transitions",
    body: "How the physician became an operator. The four phases of organizational evolution. The decade of building that produced a Digital Conglomerate.",
    accent: "purple",
  },
  {
    icon: Target,
    title: "Operational Excellence",
    body: "The engineered system that produces verified outcomes. The five-step methodology, the review cycle, the discipline of building rather than inspiring.",
    accent: "cyan",
  },
  {
    icon: Trophy,
    title: "Triple Threat",
    body: "The intersection of medicine, technology, and politics in one operator. The capabilities no conventional executive possesses — and why it matters.",
    accent: "emerald",
  },
  {
    icon: Heart,
    title: "The 70% Operating Model",
    body: "Why women constitute over seventy percent of most offices. The operating advantages — communication, risk discipline, collaboration, retention, ego management.",
    accent: "lime",
  },
  {
    icon: Mic,
    title: "Speaking & Engagements",
    body: "Signature topics, booking process, format options, and stage presence across conferences, corporate events, and institutional gatherings.",
    accent: "amber-gold",
  },
  {
    icon: Brain,
    title: "Philosophy & Vision",
    body: "Excellence as an operating system, not a value. Building in the dark. The discipline of constructing consequential things away from public attention.",
    accent: "denim",
  },
  {
    icon: Lock,
    title: "Engagement & Confidentiality",
    body: "How to initiate contact, the discovery process, the engagement model, and the national security level confidentiality that governs every interaction.",
    accent: "coral",
  },
];

export default function FAQHubPage() {
  return (
    <>
      <PageHero
        icon={HelpCircle}
        overline="Frequently Asked Questions"
        overlineClassName="text-cyan"
        title={
          <>
            Every Question Answered. <span className="text-gradient-premium">Every Claim Verified.</span>
          </>
        }
        subtitle="The most comprehensive FAQ universe for any executive operating at the sovereign level. Eight subpages, two hundred and fifty-seven verified questions, every answer grounded in operational data — not fabricated metrics, not speculative claims."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "FAQ" }]}
        badge="257 verified questions · 15+ years"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
            <Sparkles className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Short Answer</span>
          </div>
          <h2 className="text-display-md mb-5">Who Dr. Jyoti Kush is in one paragraph.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Dr. Jyoti Kush is the Chief Operating Officer of CryptoMize and MaxiMize Infinium — a Digital Conglomerate operating at the intersection of AI-powered intelligence, military-grade security, and political-grade strategy across eighteen countries on three continents. She holds an MBBS degree with a specialization in Gynaecology from Delhi. She co-built the organization over a decade, evolving it from an information privacy consultancy into a full-spectrum strategic sovereignty provider. The teams are over seventy percent women. The operational record includes fifteen verified campaign victories out of eighteen engagements, over three hundred elite clients served, and zero security incidents across fifteen-plus years. Presence at the Nobel Peace Prize and the World Economic Forum.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <MessageSquare className="size-3.5 text-red" />
            <span className="text-overline text-red">People Also Ask</span>
          </div>
          <h2 className="text-display-md mb-4">Quick answers to the most common questions.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight high-frequency questions with verified answers. The full breakdowns follow below — eight subpages covering two hundred and fifty-seven questions in total.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {HEADLINE_QUESTIONS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{s.q}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.a}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <BookOpen className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Eight Subpages</span>
          </div>
          <h2 className="text-display-md mb-4">Explore the complete FAQ universe.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each subpage addresses a distinct domain of expertise with the same rigor, specificity, and verified data. Together, they constitute the most comprehensive FAQ resource for any executive operating at the sovereign level.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FAQ_SUBPAGES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={p.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-6 h-full overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative">
                      <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg ring-2 ${a.ring}`}>
                        <p.icon className="size-6 text-white" />
                      </div>
                      <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-4">{p.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-caption font-semibold text-muted-foreground">{p.questions} questions</span>
                        <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                          Explore <ArrowRight className="size-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Compass className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Question Categories</span>
          </div>
          <h2 className="text-display-md mb-4">Every domain of inquiry, organized.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The questions span eight thematic categories — from clinical method to engagement process — each reflecting a dimension of the operational philosophy and verified track record.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg`}>
                  <c.icon className="size-5 text-white" />
                </div>
                <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{c.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Activity className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Verified Metrics</span>
          </div>
          <h2 className="text-display-md mb-4">The numbers behind every answer.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every claim on every FAQ page traces to verified operational data. The metrics below represent fifteen-plus years of sovereign-scale delivery — not projections, not targets.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { metric: "15", suffix: "/18", label: "Campaign Victories", sub: "83.3% success rate", accent: "red" },
            { metric: "300", suffix: "+", label: "Elite Clients Served", sub: "Nine client categories", accent: "purple" },
            { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
            { metric: "99.9999", suffix: "%", label: "Operational Uptime", sub: "31.5 seconds max/year", accent: "denim" },
            { metric: "70", suffix: "%+", label: "Women in Teams", sub: "Operating model, not quota", accent: "lime" },
            { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "amber-gold" },
            { metric: "15", suffix: "+", label: "Years Operational", sub: "Sovereign-scale delivery", accent: "coral" },
            { metric: "22", suffix: "", label: "Official Languages", sub: "900M+ voters reached", accent: "cyan" },
          ].map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <MessageSquare className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Still have a question?</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every question receives a confirmation of receipt, typically within twenty-four hours. The diagnostic phase — understanding the situation before prescribing the engagement — is the foundation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Reach Out
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary of Terms", description: "Definitional reference for the Penta P's framework, sovereign operations, and the operational vocabulary.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect of sovereign-scale operations.", href: "/about/", icon: Crown, accent: "red" },
          { title: "Services & Engagements", description: "Executive advisory, board service, consulting, and operational methodologies.", href: "/services/", icon: Briefcase, accent: "purple" },
          { title: "The Operator's Notebook", description: "Biweekly insights on how excellent things run. One principle, made usable.", href: "/the-operators-notebook/", icon: PenLine, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
